import { domain } from "../auth/domain";
import decodeJwtToken from "../auth/decodeJwtToken";
import { normalizePhoneNumber } from "./authService";

const getToken = () => localStorage.getItem("accessToken");

const getAuthHeaders = () => {
  const token = getToken();
  if (!token) {
    throw new Error("No access token found.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `JWT ${token}`,
  };
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.append(key, value);
  });
  return query.toString();
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json();
    const errorMessages = Object.entries(error)
      .map(([key, value]) => {
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const message = Array.isArray(value) ? value.join(', ') : value;
        return `${formattedKey}: ${message}`;
      })
      .join('; ');
    throw new Error(errorMessages || "An API error occurred");
  }
  return response.status === 204 ? null : response.json();
};

const getGroupId = () => {
    const token = getToken();
    if (!token) return null;
    const decoded = decodeJwtToken(token);
    return decoded?.group_id;
}

export const fetchMembers = async () => {
  // Fetch members of the group the current logged in user belongs to
  const groupId = getGroupId();
  if (!groupId) throw new Error("User is not associated with a group.");

  const response = await fetch(`${domain}groups/${groupId}/members/`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};


export const fetchAnyGroupMembers = async (groupId) => {
  // Fetch members of the any group by ID
  if (!groupId) throw new Error("Select a group.");

  const response = await fetch(`${domain}groups/${groupId}/members/`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const createMember = async (memberData) => {
  const groupId = getGroupId();
  if (!groupId) throw new Error("User is not associated with a group.");

  // Transform frontend form data to match backend serializer expectations
  const normalizedIdentity =
    memberData.identity_type === "NIDA"
      ? (memberData.identity_number || "").replace(/\D/g, "")
      : (memberData.identity_number || "").toUpperCase();
  const payload = {
    full_name: memberData.name,
    phone_number: normalizePhoneNumber(memberData.phone || ""),
    email: memberData.email,
    role: memberData.role,
    number_of_slots: memberData.number_of_slots,
    nmb_account_number: memberData.nmb_account_number,
    identity_type: memberData.identity_type,
    identity_number: normalizedIdentity,
    // contribution_amount: memberData.contribution_amount, // This field is for group creation, not member creation
    heir_data: memberData.heir,
  };

  const response = await fetch(`${domain}groups/${groupId}/members/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const updateMember = async (memberId, memberData) => {
  const groupId = getGroupId();
  if (!groupId) throw new Error("User is not associated with a group.");

  const normalizedIdentity =
    memberData.identity_type === "NIDA"
      ? (memberData.identity_number || "").replace(/\D/g, "")
      : (memberData.identity_number || "").toUpperCase();
  const payload = {
    ...memberData,
    identity_number: normalizedIdentity,
  };
  if (!payload.heir_data) {
    delete payload.heir_data;
  }

  const response = await fetch(`${domain}groups/${groupId}/members/${memberId}/`, {
    method: "PATCH", // Use PATCH for partial updates
    headers: getAuthHeaders(),
    body: JSON.stringify({
      ...payload,
      user: memberData.user
        ? {
            ...memberData.user,
            phone_number: normalizePhoneNumber(memberData.user.phone_number || ""),
          }
        : undefined,
    }),
  });
  return handleResponse(response);
};

export const deleteMember = async (memberId) => {
  const groupId = getGroupId();
  if (!groupId) throw new Error("User is not associated with a group.");

  const response = await fetch(`${domain}groups/${groupId}/members/${memberId}/`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const fetchSingleMemberDetails = async (memberId) => {
  const groupId = getGroupId();
  if (!groupId) throw new Error("User is not associated with a group.");

  const response = await fetch(`${domain}groups/${groupId}/members/${memberId}/`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const submitMemberContribution = async (memberId, amount) => {
  const groupId = getGroupId();
  if (!groupId) throw new Error("User is not associated with a group.");

  const response = await fetch(`${domain}groups/${groupId}/members/${memberId}/contribute/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ amount }),
  });
  return handleResponse(response);
};


export const changeMemberPassword = async (userId, newPassword) => {
  const response = await fetch(`${domain}auth/users/${userId}/`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ password: newPassword }),
  });
  return handleResponse(response);
};

export const resetMemberPassword = async (memberId) => {
  const groupId = getGroupId();
  if (!groupId) throw new Error("User is not associated with a group.");

  const response = await fetch(`${domain}groups/${groupId}/members/${memberId}/reset-password/`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const fetchMemberTransactions = async (memberId, options = {}) => {
  const groupId = getGroupId();
  if (!groupId) throw new Error("User is not associated with a group.");

  const query = buildQuery(options);
  const endpoint = query
    ? `${domain}groups/${groupId}/members/${memberId}/transactions/?${query}`
    : `${domain}groups/${groupId}/members/${memberId}/transactions/`;
  const response = await fetch(endpoint, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};
